/**
 * 📊 V208.0: PostHog Analytics Client-Side Wrapper
 *
 * Fail-safe analytics implementation that gracefully degrades when PostHog is unavailable.
 * The app MUST continue working even if PostHog API is down or blocked.
 *
 * V209.10: PostHog uses dynamic import to prevent bundling on /auth route
 * V208.0 Hardening: Kill switch, no init before consent, PII-free, fail-safe.
 *
 * === V334 VALIDATION CHECKLIST ===
 * 1. DevTools Network tab:
 *    - Before consent: No requests to eu.i.posthog.com (no /e/, /flags/, /capture/)
 *    - After Accept: POST to eu.i.posthog.com/e/ and eu.i.posthog.com/flags/
 * 2. PostHog Live Events (https://eu.posthog.com):
 *    - Filter for blissnet_posthog_verification_ping or app_opened
 *    - Verify no PII (email, name, phone) in payloads
 * 3. Console verification ping (after consent):
 *    - window.analyticsDebug.trackPosthogVerificationPing()
 *    - Event appears in Live Events within ~5s
 */

// V208.0: Kill switch — analytics OFF unless explicitly enabled via env
const ANALYTICS_ENABLED = import.meta.env.VITE_ANALYTICS_ENABLED === 'true';
// Analytics is OFF unless explicitly enabled via env.
// Missing or empty VITE_ANALYTICS_ENABLED → disabled.

// V209.10: Dynamic import - posthog-js not bundled until actually needed
let posthog: any = null;
let posthogModule: any = null;

async function loadPostHog() {
  if (posthogModule) return posthogModule;
  
  try {
    posthogModule = await import('posthog-js');
    posthog = posthogModule.default;
    return posthogModule;
  } catch (error) {
    console.warn('📊 V209.10: Failed to load posthog-js:', error);
    return null;
  }
}

import { IS_WEB, IS_ANDROID } from '@/config/channel';
import { AnalyticsEvents, type AnalyticsEventName } from './events';
import { buildBaseProperties, buildUserProperties } from './properties';

// PostHog React Native (for Android) - lazy loaded to avoid web build issues
let PostHogReactNative: any = null;

function getPostHogReactNative() {
  if (!IS_ANDROID) return null;
  
  if (PostHogReactNative === null) {
    try {
      PostHogReactNative = require('posthog-react-native');
    } catch (error) {
      console.warn('📊 V208.0: PostHog React Native not available:', error);
      PostHogReactNative = false; // Mark as failed to prevent retries
    }
  }
  
  return PostHogReactNative || null;
}

// Configuration
const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY;
// 🚀 V208.0: EU Region - PostHog project is in EU region
// Ingestion host: https://eu.i.posthog.com
// Dashboard UI: https://eu.posthog.com
const POSTHOG_HOST = import.meta.env.VITE_POSTHOG_HOST || 'https://eu.i.posthog.com';
const POSTHOG_UI_HOST = 'https://eu.posthog.com'; // EU dashboard UI

// 🚀 V208.0: Defensive check - warn if wrong region detected
if (POSTHOG_HOST && POSTHOG_HOST.includes('us.i.posthog.com')) {
  console.warn('⚠️ PostHog: EU project detected — ensure host is https://eu.i.posthog.com');
}

// State
let isInitialized = false;
let isEnabled = false;
let hasConsent = false; // GDPR consent (web only)
let posthogInitialized = false; // V208.0: Prevents double init (Android + Web edge case)

/**
 * Check if user has given consent for analytics (web only)
 */
function checkConsent(): boolean {
  if (!IS_WEB) return true; // Android doesn't need cookie consent
  
  try {
    const consent = localStorage.getItem('posthog_consent');
    return consent === 'accepted';
  } catch {
    return false;
  }
}

/**
 * Initialize PostHog (fail-safe)
 * V209.8: Disable surveys on /auth route to reduce bundle size
 * V209.10: Now async - dynamically loads posthog-js
 */
export async function initializePostHog(): Promise<void> {
  if (!ANALYTICS_ENABLED) return;

  if (isInitialized) {
    console.warn('📊 V208.0: PostHog already initialized');
    return;
  }

  // Check if PostHog key is configured
  if (!POSTHOG_KEY) {
    console.warn('📊 V208.0: PostHOG_KEY not configured - analytics disabled');
    isInitialized = true; // Mark as initialized to prevent retries
    return;
  }

  // V209.8: Skip PostHog initialization on /auth route to prevent surveys bundle from loading
  if (IS_WEB && typeof window !== 'undefined') {
    const isAuthRoute = window.location.pathname === '/auth' || window.location.pathname.startsWith('/auth');
    if (isAuthRoute) {
      console.log('📊 V209.8: Skipping PostHog initialization on /auth route (surveys disabled)');
      isInitialized = true; // Mark as initialized to prevent retries
      return;
    }
  }

  // Check consent (web only)
  if (IS_WEB) {
    hasConsent = checkConsent();
    if (!hasConsent) {
      console.log('📊 V208.0: Waiting for user consent before initializing PostHog');
      isInitialized = true; // Mark as initialized, will enable when consent given
      return;
    }
  }

  try {
    if (posthogInitialized) return;

    if (IS_WEB) {
      // V209.10: Dynamically load posthog-js (not bundled until needed)
      const phModule = await loadPostHog();
      if (!phModule || !phModule.default) {
        console.warn('📊 V209.10: PostHog module not available');
        isInitialized = true;
        return;
      }
      
      const ph = phModule.default;
      
      // Web: Use posthog-js
      // 🚀 V208.0: EU Region - api_host for ingestion, ui_host for dashboard
      // V209.8: Disable surveys to reduce bundle size
      ph.init(POSTHOG_KEY, {
        api_host: POSTHOG_HOST, // EU ingestion: https://eu.i.posthog.com
        ui_host: POSTHOG_UI_HOST, // EU dashboard: https://eu.posthog.com
        loaded: () => {
          console.log('📊 V208.0: PostHog initialized (web) - EU region');
          isEnabled = true;
          trackEvent(AnalyticsEvents.APP_OPENED, { platform: 'web' }).catch(() => {});
        },
        capture_pageview: false, // We'll track pageviews manually
        capture_pageleave: false,
        autocapture: false, // Disable autocapture for privacy
        disable_session_recording: true, // Disable session recording for privacy
        respect_dnt: true, // Respect Do Not Track
        // 🚀 V208.0: Additional configuration to prevent errors
        advanced_disable_decide: false, // Enable feature flags
        disable_persistence: false, // Enable local storage
        persistence: 'localStorage+cookie', // Use both storage methods
        // V209.8: Disable surveys to prevent surveys.js bundle from loading
        disable_surveys: true, // Disable PostHog surveys feature
      });
      posthogInitialized = true;
    } else if (IS_ANDROID) {
      // Android: Use posthog-react-native
      const ph = getPostHogReactNative();
      if (ph) {
        ph.setup(POSTHOG_KEY, {
          host: POSTHOG_HOST,
          enableSessionReplay: false, // Disable session replay for privacy
        });
        posthogInitialized = true;
        isInitialized = true;
        console.log('📊 V208.0: PostHog initialized (Android)');
        isEnabled = true;
        trackEvent(AnalyticsEvents.APP_OPENED, { platform: 'android' }).catch(() => {});
      } else {
        console.warn('📊 V208.0: PostHog React Native not available for Android');
      }
    }
    
    isInitialized = true;
  } catch (error) {
    // Fail silently - analytics should never break the app
    console.warn('📊 V208.0: PostHog initialization failed (non-blocking):', error);
    isInitialized = true; // Mark as initialized to prevent retries
  }
}

/**
 * Enable PostHog after consent is given (web only)
 * V209.10: Now async - dynamically loads posthog if needed
 */
export async function enablePostHog(): Promise<void> {
  if (!ANALYTICS_ENABLED) return;
  if (!IS_WEB) return;
  
  hasConsent = true;
  
  try {
    localStorage.setItem('posthog_consent', 'accepted');
  } catch (error) {
    console.warn('📊 V208.0: Failed to save consent:', error);
  }
  
  // Initialize if not already done
  if (!isInitialized) {
    await initializePostHog();
  } else if (isInitialized && !isEnabled) {
    // Re-initialize with consent
    isInitialized = false;
    await initializePostHog();
  } else if (isInitialized && isEnabled) {
    // User re-enabled after opt-out: call opt_in_capturing
    try {
      if (IS_WEB && posthog) {
        posthog.opt_in_capturing?.();
      } else if (IS_WEB && !posthog) {
        await loadPostHog();
        posthog?.opt_in_capturing?.();
      } else if (IS_ANDROID) {
        getPostHogReactNative()?.optIn?.();
      }
    } catch {
      // Fail silently
    }
  }
}

/**
 * Track an event (fail-safe)
 */
export async function trackEvent(
  eventName: AnalyticsEventName | string,
  properties?: Record<string, any>
): Promise<void> {
  if (!ANALYTICS_ENABLED) return;
  // Fail silently if PostHog is not available
  if (!isInitialized || !isEnabled || !hasConsent) {
    if (import.meta.env.DEV) {
      console.log('📊 V208.0: Event not tracked (PostHog not available):', eventName, properties);
    }
    return;
  }

  // V209.10: Dynamically load posthog if not already loaded
  if (!posthog) {
    await loadPostHog();
  }

  try {
    const baseProps = buildBaseProperties();
    const eventProperties = {
      ...baseProps,
      ...properties,
    };

    if (IS_WEB) {
      // V209.10: Ensure posthog is loaded before using
      if (!posthog) {
        await loadPostHog();
      }
      if (posthog) {
        posthog.capture(eventName, eventProperties);
      }
    } else if (IS_ANDROID) {
      const ph = getPostHogReactNative();
      if (ph) {
        ph.capture(eventName, eventProperties);
      }
    }
  } catch (error) {
    // Fail silently - analytics should never break the app
    console.warn('📊 V208.0: Event tracking failed (non-blocking):', error);
  }
}

/**
 * Identify a user (fail-safe)
 * V209.10: Now async - dynamically loads posthog if needed
 */
export async function identifyUser(
  userId: string,
  traits?: Record<string, any>
): Promise<void> {
  if (!ANALYTICS_ENABLED) return;
  // Fail silently if PostHog is not available
  if (!isInitialized || !isEnabled || !hasConsent) {
    if (import.meta.env.DEV) {
      console.log('📊 V208.0: User identification skipped (PostHog not available):', userId);
    }
    return;
  }

  try {
    const safeTraits = buildUserProperties(userId, traits);

    if (IS_WEB) {
      // V209.10: Ensure posthog is loaded before using
      if (!posthog) {
        await loadPostHog();
      }
      if (posthog) {
        posthog.identify(userId, safeTraits);
      }
    } else if (IS_ANDROID) {
      const ph = getPostHogReactNative();
      if (ph) {
        ph.identify(userId, safeTraits);
      }
    }
  } catch (error) {
    // Fail silently - analytics should never break the app
    console.warn('📊 V208.0: User identification failed (non-blocking):', error);
  }
}

/**
 * Opt out of capturing (Settings toggle disable)
 * V208.0: Stops all future events until user re-enables.
 */
export async function optOutCapturing(): Promise<void> {
  if (!ANALYTICS_ENABLED) return;
  hasConsent = false; // Ensure trackEvent/identifyUser no-op
  try {
    if (IS_WEB && posthog) {
      posthog.opt_out_capturing?.();
    } else if (IS_WEB && !posthog) {
      const phModule = await loadPostHog();
      if (phModule?.default) {
        phModule.default.opt_out_capturing?.();
      }
    } else if (IS_ANDROID) {
      const ph = getPostHogReactNative();
      ph?.optOut?.();
    }
  } catch {
    // Fail silently
  }
}

/**
 * Reset user identification (on logout)
 * V209.10: Now async - dynamically loads posthog if needed
 */
export async function resetUser(): Promise<void> {
  if (!ANALYTICS_ENABLED) return;
  if (!isInitialized || !isEnabled) return;

  try {
    if (IS_WEB) {
      // V209.10: Ensure posthog is loaded before using
      if (!posthog) {
        await loadPostHog();
      }
      if (posthog) {
        posthog.reset();
      }
    } else if (IS_ANDROID) {
      const ph = getPostHogReactNative();
      if (ph) {
        ph.reset();
      }
    }
  } catch (error) {
    console.warn('📊 V208.0: User reset failed (non-blocking):', error);
  }
}

/**
 * V334: Verification ping — ONE controlled test event for PostHog Live Events validation.
 * Call from console after consent: window.analyticsDebug.trackPosthogVerificationPing()
 * No PII. Uses existing consent gate (no-op if consent not given).
 */
export async function trackPosthogVerificationPing(): Promise<void> {
  const channel = IS_WEB ? 'web' : IS_ANDROID ? 'android' : 'unknown';
  await trackEvent('blissnet_posthog_verification_ping', { channel });
}

/**
 * Track page view
 * V209.10: Now async - uses async trackEvent
 */
export async function trackPageView(pageName: string, properties?: Record<string, any>): Promise<void> {
  await trackEvent(AnalyticsEvents.PAGE_VIEWED, {
    page_name: pageName,
    ...properties,
  });
}

// Export event constants and types for convenience
export { AnalyticsEvents };
export type { ScreenName } from './events';

// V209.10: PostHog initialization moved to MainAppShell
// No auto-initialization here to prevent bundling posthog-js into /auth route
// PostHog will be initialized in MainAppShell.useEffect (only on authenticated routes)

// 📊 V208.0/V334.1: Debug helper for console — always on web (dev + prod) for verification ping
if (IS_WEB && typeof window !== 'undefined') {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).analyticsDebug = {
    testEvent: () => {
      trackEvent(AnalyticsEvents.TEST_EVENT, {
        test_property: 'hello_from_v208',
        test_source: 'console_debug',
        test_timestamp: new Date().toISOString(),
      });
      console.log('📊 V208.0: Test event fired via console - check PostHog Live Events');
    },
    identifyTestUser: async (userId: string = `test-user-${Date.now()}`) => {
      await identifyUser(userId, {
        plan: 'free',
        profile_completed: true,
        test_user: true,
      });
      console.log(`📊 V208.0: Test user identified: ${userId} - check PostHog Persons`);
    },
    trackPosthogVerificationPing: () => {
      trackPosthogVerificationPing()
        .then(() =>
          console.log('📊 V334: Verification ping attempted. If consent accepted, check PostHog Live Events for blissnet_posthog_verification_ping.')
        )
        .catch((err) =>
          console.warn('📊 V334: Verification ping skipped (analytics disabled or consent not granted):', err)
        );
    },
  };
  if (import.meta.env.DEV) {
    console.log('📊 V208.0: Analytics debug helper: window.analyticsDebug.trackPosthogVerificationPing()');
  }
}
